import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../util/auth';
import { updateUser, getUser } from '../../util/db'; // Update the import path as needed
import '../../styles/OnboardingForm.css';
import LoadingIcon from '../cosmetics/LoadingIcon';
import { UserCircleIcon, ChartBarIcon, DocumentTextIcon, PresentationChartLineIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const goals = [
  {
    id: 'benchmarking',
    title: 'Benchmarking',
    description: 'Compare my people metrics against industry standards',
    icon: ChartBarIcon
  },
  {
    id: 'data_consolidation',
    title: 'Data Consolidation',
    description: 'Bring all my people data into one place',
    icon: DocumentTextIcon
  },
  {
    id: 'reporting',
    title: 'Reporting & Insights',
    description: 'Create and share reports with leadership and teams',
    icon: PresentationChartLineIcon
  },
  {
    id: 'continuous_improvement',
    title: 'Continuous Improvement',
    description: 'Track and improve my people metrics over time',
    icon: ArrowPathIcon
  }
];

export default function PersonalDetailsForm({ onNextStep, onBack }) {
  const auth = useAuth();
  const ownerId = auth.user?.id;
  const [pending, setPending] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [selectedGoal, setSelectedGoal] = useState(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    goal: ''
  });

  // Track if form has been interacted with
  const [hasInteracted, setHasInteracted] = useState(false);

  useEffect(() => {
    if (ownerId) {
      getUser(ownerId).then((userData) => {
        if (userData) {
          setFormData({
            firstName: userData.first_name || '',
            lastName: userData.last_name || '',
            goal: userData.goal || ''
          });
          // Set the selected goal from the database
          if (userData.goal) {
            setSelectedGoal(userData.goal);
          }
        }
      });
    }
  }, [ownerId]);

  const { register, handleSubmit, errors } = useForm();

  const onSubmit = async (data) => {
    if (!ownerId) {
      console.error('No user ID available for updating user data');
      return;
    }
    if (!selectedGoal) {
      return; // Don't submit if no goal is selected
    }
    setPending(true);

    try {
      const response = await updateUser(ownerId, {
        first_name: data.firstName,
        last_name: data.lastName,
        goal: selectedGoal
      });

      if (response && response.error) {
        console.error('Error updating user:', response.error);
      } else {
        onNextStep();
      }
    } catch (error) {
      console.error('An unexpected error occurred:', error);
    } finally {
      setPending(false);
    }
  };

  // Calculate form completion percentage
  const getCompletionPercentage = () => {
    let completed = 0;
    if (formData.firstName) completed++;
    if (formData.lastName) completed++;
    if (formData.goal) completed++;
    return (completed / 3) * 100;
  };

  return (
    <div className="max-w-2xl mx-auto px-4">
      {/* Welcome Section */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-50 mb-4">
          <UserCircleIcon className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Hi there! 👋
        </h1>
        <p className="text-gray-500 text-lg">
          Let's get to know you better
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Progress Bar */}
        {hasInteracted && (
          <div className="mb-6">
            <div className="h-1 w-full bg-gray-100 rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500 ease-out"
                style={{ width: `${getCompletionPercentage()}%` }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name Input */}
          <div className={`relative transition-all duration-200 ${focusedField === 'firstName' ? 'transform scale-[1.02]' : ''}`}>
            <label 
              htmlFor="firstName"
              className={`absolute left-3 transition-all duration-200 ${
                focusedField === 'firstName' || formData.firstName 
                  ? '-top-2.5 text-xs text-indigo-600 bg-white px-2' 
                  : 'top-3.5 text-gray-400'
              }`}
            >
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              ref={register({ required: 'First name is required' })}
              className={`
                block w-full px-3 py-3 rounded-xl border-2 bg-white
                transition-all duration-200
                ${focusedField === 'firstName' 
                  ? 'border-indigo-600 shadow-lg shadow-indigo-100' 
                  : 'border-gray-200 hover:border-gray-300'
                }
                ${errors.firstName ? 'border-red-300' : ''}
              `}
              value={formData.firstName}
              onChange={(e) => {
                setFormData({ ...formData, firstName: e.target.value });
                setHasInteracted(true);
              }}
              onFocus={() => setFocusedField('firstName')}
              onBlur={() => setFocusedField(null)}
              placeholder=""
            />
            {errors.firstName && (
              <p className="mt-1 text-sm text-red-500 pl-3">{errors.firstName.message}</p>
            )}
          </div>

          {/* Last Name Input */}
          <div className={`relative transition-all duration-200 ${focusedField === 'lastName' ? 'transform scale-[1.02]' : ''}`}>
            <label 
              htmlFor="lastName"
              className={`absolute left-3 transition-all duration-200 ${
                focusedField === 'lastName' || formData.lastName 
                  ? '-top-2.5 text-xs text-indigo-600 bg-white px-2' 
                  : 'top-3.5 text-gray-400'
              }`}
            >
              Last Name
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              ref={register({ required: 'Last name is required' })}
              className={`
                block w-full px-3 py-3 rounded-xl border-2 bg-white
                transition-all duration-200
                ${focusedField === 'lastName' 
                  ? 'border-indigo-600 shadow-lg shadow-indigo-100' 
                  : 'border-gray-200 hover:border-gray-300'
                }
                ${errors.lastName ? 'border-red-300' : ''}
              `}
              value={formData.lastName}
              onChange={(e) => {
                setFormData({ ...formData, lastName: e.target.value });
                setHasInteracted(true);
              }}
              onFocus={() => setFocusedField('lastName')}
              onBlur={() => setFocusedField(null)}
              placeholder=""
            />
            {errors.lastName && (
              <p className="mt-1 text-sm text-red-500 pl-3">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        {/* Goals Selection */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">What's your main goal?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {goals.map((goal) => (
              <div
                key={goal.id}
                onClick={() => {
                  setSelectedGoal(goal.id);
                  setFormData(prev => ({ ...prev, goal: goal.id }));
                  setHasInteracted(true);
                }}
                className={`group p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 ease-in-out transform hover:-translate-y-0.5 ${
                  selectedGoal === goal.id
                    ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-white shadow-sm ring-1 ring-indigo-100'
                    : 'border-gray-200 hover:border-indigo-300 bg-white hover:shadow-lg'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`p-2.5 rounded-lg transition-all duration-300 ${
                    selectedGoal === goal.id 
                      ? 'bg-gradient-to-br from-indigo-100 to-indigo-50 shadow-sm ring-1 ring-indigo-100' 
                      : 'bg-gray-50 group-hover:bg-indigo-50'
                  }`}>
                    {React.createElement(goal.icon, { 
                      className: `w-5 h-5 transition-colors duration-300 ${
                        selectedGoal === goal.id ? 'text-indigo-600' : 'text-gray-600 group-hover:text-indigo-500'
                      }`
                    })}
                  </div>
                  <div className="flex-1">
                    <h4 className={`font-medium transition-colors duration-300 ${
                      selectedGoal === goal.id ? 'text-indigo-700' : 'text-gray-900 group-hover:text-indigo-600'
                    }`}>
                      {goal.title}
                    </h4>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed transition-colors duration-300 group-hover:text-gray-600">
                      {goal.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-center space-x-4">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="back-button"
            >
              Back
            </button>
          )}
          <button
            type="submit"
            className="submit-button"
            disabled={pending || !selectedGoal}
          >
            {pending ? <LoadingIcon className="w-6" /> : "Continue"}
          </button>
        </div>
      </form>
    </div>
  );
}
